import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { UserPlus, Loader2, MessageCircle } from "lucide-react";

const SOURCES = ["whatsapp", "phone", "referral", "instagram", "manual"];
const CAMPUSES = ["general", "santa_ana", "atenas", "grecia", "online"];

/**
 * ManualLeadDialog — button + modal to add a lead by hand.
 * Used for prospects that reached the school through WhatsApp, phone, walk-in
 * or word-of-mouth referral, so the coordinator can track them in the same Kanban.
 */
export default function ManualLeadDialog({ onCreated }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    last_name: "",
    email: "",
    whatsapp: "",
    campus: "santa_ana",
    group: "",
    referred: "",
    message: "",
    source: "whatsapp",
    initial_note: "",
    send_auto_response: false,
    template_id: "auto",
  });
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    if (!open || templates.length) return;
    api.get("/lead-templates")
      .then(({ data }) => setTemplates(data?.items || []))
      .catch(() => setTemplates([]));
  }, [open, templates.length]);

  const tplValue = (tpl) => (tpl.id ? tpl.id : `default:${tpl.campus}`);
  const tplLabel = (tpl) => {
    const prog = tpl.program || t("manual_lead_template_general");
    const tag = tpl.is_default ? ` · ${t("manual_lead_template_default")}` : "";
    return `${tpl.campus_label} · ${prog}${tag}`;
  };

  const reset = () => setForm({
    name: "", last_name: "", email: "", whatsapp: "", campus: "santa_ana", group: "",
    referred: "", message: "", source: "whatsapp", initial_note: "",
    send_auto_response: false, template_id: "auto",
  });

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error(t("manual_lead_missing_name"));
      return;
    }
    if (!form.email.trim() && !form.whatsapp.trim()) {
      toast.error(t("manual_lead_missing_contact"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        last_name: form.last_name.trim() || null,
        email: form.email.trim() || null,
        whatsapp: form.whatsapp.trim() || null,
        campus: form.campus || null,
        group: form.group.trim() || null,
        referred: form.referred.trim() || null,
        message: form.message.trim() || null,
        source: form.source,
        initial_note: form.initial_note.trim() || null,
        send_auto_response: form.send_auto_response,
        template_id: form.template_id === "auto" ? null : form.template_id,
      };
      const { data } = await api.post("/leads/manual", payload);
      toast.success(t("manual_lead_created"));
      setOpen(false);
      reset();
      onCreated && onCreated(data.lead_id);
    } catch (e) {
      const msg = e?.response?.data?.detail || t("manual_lead_error");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="manual-lead-btn">
        <UserPlus className="h-4 w-4 mr-1.5" />
        {t("manual_lead_add")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="manual-lead-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-emerald-600" />
              {t("manual_lead_title")}
            </DialogTitle>
            <DialogDescription>{t("manual_lead_desc")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("manual_lead_name")} *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="María"
                  className="mt-1"
                  data-testid="manual-lead-name"
                />
              </div>
              <div>
                <Label>{t("manual_lead_last_name")}</Label>
                <Input
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                  placeholder="Rodríguez"
                  className="mt-1"
                  data-testid="manual-lead-last-name"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("manual_lead_whatsapp")}</Label>
                <Input
                  value={form.whatsapp}
                  onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                  placeholder="+506 8888 1234"
                  className="mt-1"
                  data-testid="manual-lead-whatsapp"
                />
              </div>
              <div>
                <Label>{t("manual_lead_email")}</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="maria@example.com"
                  className="mt-1"
                  data-testid="manual-lead-email"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("lead_status")}</Label>
                <Select value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                  <SelectTrigger className="mt-1" data-testid="manual-lead-source">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SOURCES.map((s) => (
                      <SelectItem key={s} value={s}>{t(`lead_source_${s}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("lead_campus")}</Label>
                <Select value={form.campus} onValueChange={(v) => setForm({ ...form, campus: v })}>
                  <SelectTrigger className="mt-1" data-testid="manual-lead-campus">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CAMPUSES.map((c) => (
                      <SelectItem key={c} value={c}>{t(`lead_campus_${c}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>{t("lead_program")}</Label>
              <Input
                value={form.group}
                onChange={(e) => setForm({ ...form, group: e.target.value })}
                placeholder="Classes for Beginners A1, Mon/Wed at 8:15 am"
                className="mt-1"
                data-testid="manual-lead-group"
              />
            </div>
            <div>
              <Label>{t("lead_referred")}</Label>
              <Input
                value={form.referred}
                onChange={(e) => setForm({ ...form, referred: e.target.value })}
                placeholder="Google, amiga, exalumno..."
                className="mt-1"
                data-testid="manual-lead-referred"
              />
            </div>
            <div>
              <Label>{t("manual_lead_initial_note")}</Label>
              <Textarea
                value={form.initial_note}
                onChange={(e) => setForm({ ...form, initial_note: e.target.value })}
                placeholder={t("manual_lead_initial_note_placeholder")}
                rows={2}
                className="mt-1"
                data-testid="manual-lead-initial-note"
              />
            </div>
            {form.email && (
              <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-3 space-y-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer text-slate-700 font-medium">
                  <input
                    type="checkbox"
                    checked={form.send_auto_response}
                    onChange={(e) => setForm({ ...form, send_auto_response: e.target.checked })}
                    data-testid="manual-lead-send-auto"
                  />
                  {t("manual_lead_send_auto")}
                </label>
                {form.send_auto_response && (
                  <div>
                    <Label className="text-xs text-slate-600">{t("manual_lead_template")}</Label>
                    <Select
                      value={form.template_id}
                      onValueChange={(v) => setForm({ ...form, template_id: v })}
                    >
                      <SelectTrigger className="mt-1 bg-white" data-testid="manual-lead-template">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">{t("manual_lead_template_auto")}</SelectItem>
                        {templates.map((tpl) => (
                          <SelectItem key={tplValue(tpl)} value={tplValue(tpl)}>
                            {tplLabel(tpl)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="mt-1.5 text-xs text-slate-500">{t("manual_lead_template_hint")}</p>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("template_cancel")}</Button>
            <Button
              onClick={submit}
              disabled={saving || !form.name.trim() || (!form.email.trim() && !form.whatsapp.trim())}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="manual-lead-submit"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <UserPlus className="h-4 w-4 mr-1.5" />}
              {t("manual_lead_create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
